/**
 * Lagerorte (KONZEPT.md §5) -- der Baum aus Ort und Packstueck.
 *
 * Die beiden Systemorte `AUSGELIEHEN` und `IM_EINSATZ` aus §5.1 sind **keine
 * Zeilen** in diesem Baum. Sie sind virtuell, und genau das druecken wir
 * dadurch aus, dass `Zustand.lagerort_id` dann `null` ist und der Status sagt,
 * warum. Ein Scheinknoten im Baum haette denselben Zustand an zwei Stellen
 * gefuehrt -- einmal als Status, einmal als Ort.
 */
import { z } from "zod";
import { Artikel } from "./artikel.js";
import { GeraeteKennung, Id, Kennung, Kurzcode, KurzText, LangText, ThwInventarnummer, Zeitpunkt, } from "./basis.js";
import { LagerortEbene } from "./enums.js";
/**
 * Kuerzel eines Packstuecktyps, etwa `GB` fuer Gitterbox (ADR 0028). Steht
 * vorn in der Kennung: GB-09.
 */
export const PackstueckTypKuerzel = z
    .string()
    .regex(/^[A-Z0-9]{1,6}$/, "1 bis 6 Grossbuchstaben oder Ziffern, etwa GB");
/**
 * Ein Packstuecktyp (ADR 0028). Admins pflegen sie in den Stammdaten. Typen
 * werden nie geloescht, nur stillgelegt -- Packstuecke tragen sie weiter.
 * Seit Vertrag 2.12.
 */
export const PackstueckTyp = z.object({
    kuerzel: PackstueckTypKuerzel,
    bezeichnung: KurzText,
    /** Reihenfolge in der Auswahl, aufsteigend. */
    reihenfolge: z.number().int(),
    aktiv: z.boolean(),
});
export const PackstueckTypAnlegen = PackstueckTyp.omit({ aktiv: true }).extend({
    reihenfolge: z.number().int().default(100),
});
/** Das Kuerzel bleibt, es steht in Kennungen und auf Etiketten. */
export const PackstueckTypAendern = PackstueckTyp.omit({ kuerzel: true }).partial();
/** Die Packstuecktypen, stillgelegte eingeschlossen. */
export const PackstueckTypListe = z.object({ packstueck_typen: z.array(PackstueckTyp) });
/** Die Typen, mit denen jede Installation beginnt. Dieselben legt Migration 0010 an. */
export const STANDARD_PACKSTUECKTYPEN = [
    { kuerzel: "GB", bezeichnung: "Gitterbox", reihenfolge: 10, aktiv: true },
    { kuerzel: "KK", bezeichnung: "Kleinteilekiste", reihenfolge: 20, aktiv: true },
    { kuerzel: "RW", bezeichnung: "Rollwagen", reihenfolge: 30, aktiv: true },
];
export const Lagerort = z.object({
    id: Id,
    ebene: LagerortEbene,
    kennung: Kennung,
    bezeichnung: KurzText,
    /** Uebergeordneter Lagerort; `null` bei den Orten der obersten Ebene. */
    eltern_id: Id.nullable(),
    /**
     * Der Artikel, der dieser Lagerort *ist* -- bei Packstuecken.
     *
     * Ein Packstueck ist beides: ein Artikel, den man buchen und verleihen kann,
     * und ein Ort, in dem anderes Material liegt (KONZEPT.md §5.2). Dieser
     * Verweis haelt beide Seiten zusammen, ohne sie zu einer Tabelle zu
     * verschmelzen.
     */
    artikel_id: Id.nullable(),
    /**
     * Der feste Kurzcode im QR-Code (ADR 0013 §12, ADR 0017).
     *
     * Im QR steht `https://<host>/l/<kurzcode>` -- bewusst kein JSON: je kuerzer
     * der Code, desto robuster ist er beim Scannen aus Entfernung und bei
     * Schmutz (KONZEPT.md §5.3). Die Kennung steht nur als Text daneben und darf
     * sich aendern. Vergeben wird er vom Core, aendern laesst er sich nicht.
     * Seit Vertrag 2.8.
     */
    kurzcode: Kurzcode,
    /**
     * **Veraltet seit Vertrag 2.8.** War als Inhalt des QR-Codes gedacht, wird
     * aber weder gedruckt noch beim Scannen ausgewertet: Im QR steht die
     * Adresse mit dem `kurzcode`. Bleibt bis zur naechsten Hauptversion im
     * Vertrag.
     */
    qr_url: z.url().nullable(),
    /** Ja bei LKW, NEA, Anhaenger -- der Ort bewegt sich mit. */
    mobil: z.boolean(),
    /** Lagerorte werden nie geloescht, nur deaktiviert (KONZEPT.md §5.1). */
    aktiv: z.boolean(),
    /**
     * Das Standardziel beim Ausbuchen: „Ohne festen Platz“ (ADR 0013 §11).
     *
     * Hoechstens ein Lagerort traegt das Kennzeichen. Er ist ein gewoehnlicher
     * Ort -- was dort liegt, ist im Lager und verfuegbar. Gesetzt wird das
     * Kennzeichen nur von der Migration, nicht in der Verwaltung. Seit
     * Vertrag 2.5.
     */
    standardziel: z.boolean(),
    /**
     * „Unbekannt“: Hier stehen verlorene und gestohlene Teile (ADR 0020).
     *
     * Hoechstens ein Lagerort traegt das Kennzeichen, gesetzt nur von der
     * Migration. Dorthin bucht der Core selbst, sobald ein Status mit
     * `ort_unbekannt` gesetzt wird -- von Hand ist er kein Ziel und kein
     * Stammplatz. Seit Vertrag 2.9; eine lokale Kopie von vorher kennt das Feld
     * nicht.
     */
    unbekannt: z.boolean(),
    /**
     * Der Typ eines Packstuecks, etwa GB (ADR 0028). Leer bei Orten und
     * Unterebenen und bei Packstuecken, die noch keinem Typ zugeordnet sind.
     * Seit Vertrag 2.12; eine lokale Kopie von vorher kennt das Feld nicht.
     */
    packstueck_typ: PackstueckTypKuerzel.nullable(),
    notiz: LangText.nullable(),
    angelegt_am: Zeitpunkt,
});
/**
 * Lagerort aendern. Alle Felder freiwillig.
 *
 * `kennung` ist aenderbar -- sie steht auf dem Etikett, und Kennungen aendern
 * sich erfahrungsgemaess genau einmal, naemlich nach dem Pilotbetrieb
 * (KONZEPT.md §19.2). Dann soll das gehen, ohne den Lagerort neu anzulegen und
 * die Historie zu zerreissen. Der `kurzcode` bleibt dabei, deshalb laufen
 * gedruckte Etiketten nicht ins Leere; ein mitgeschickter wird verworfen.
 */
export const LagerortAendern = Lagerort.omit({
    id: true,
    angelegt_am: true,
    standardziel: true,
    unbekannt: true,
    kurzcode: true,
}).partial();
export const LagerortAnlegen = Lagerort.omit({
    id: true,
    angelegt_am: true,
    standardziel: true,
    unbekannt: true,
    kurzcode: true,
}).extend({
    eltern_id: Id.nullable().default(null),
    artikel_id: Id.nullable().default(null),
    qr_url: z.url().nullable().default(null),
    mobil: z.boolean().default(false),
    aktiv: z.boolean().default(true),
    notiz: LangText.nullable().default(null),
    packstueck_typ: PackstueckTypKuerzel.nullable().default(null),
});
export const LagerortKnoten = Lagerort.extend({
    anzahl_artikel: z.number().int().nonnegative(),
    get kinder() {
        return z.array(LagerortKnoten);
    },
});
/**
 * Ein Packstueck anlegen -- darf jeder angemeldete Benutzer, nur mit Netz
 * (ADR 0013 §7, ADR 0016).
 *
 * **Eine** Core-Operation: Lagerort und Artikel entstehen zusammen, der
 * Standort wird zugleich Stammplatz, und das Packstueck wird dort eingebucht.
 * Offline geht das nicht, weil zwei Handys sonst dieselbe Kennung vergeben und
 * schon Etiketten drucken koennten.
 *
 * - Ohne `inventarnummer` dient die **Kennung als Nummer** -- so bei etwa neun
 *   von zehn Kisten.
 * - Mit einer THW-Nummer, die es als Artikel schon gibt (aus der
 *   Ausstattungsliste), wird dieser Artikel das Packstueck. Gehoert er schon zu
 *   einem anderen, wird abgewiesen.
 * - Ist der Standort „Ohne festen Platz“, bleibt der Stammplatz leer.
 *
 * Die Einbuchung traegt Kennung und Zeitpunkt vom Geraet, wie jede Buchung
 * (CLAUDE.md §3.2). Kommt dieselbe Anfrage zweimal, ist die Kennung beim
 * zweiten Mal vergeben, und es entsteht nichts doppelt. Seit Vertrag 2.7.
 */
export const PackstueckAnlegen = z.object({
    kennung: Kennung,
    bezeichnung: KurzText,
    inventarnummer: ThwInventarnummer.nullable().default(null),
    /** Der Typ, aus dem die Kennung vorgeschlagen wurde (ADR 0028). */
    packstueck_typ: PackstueckTypKuerzel.nullable().default(null),
    /** Wo es steht -- ein aktiver Lagerort, auch ein anderes Packstueck. */
    ort_id: Id,
    notiz: LangText.nullable().default(null),
    buchung_id: Id,
    erfasst_am: Zeitpunkt,
    geraet: GeraeteKennung,
});
export const PackstueckAngelegt = z.object({
    lagerort: Lagerort,
    artikel: Artikel,
    /** Folgenummer der Einbuchung -- bis dahin holt der Abgleich das Log. */
    folge: z.number().int().positive(),
});
//# sourceMappingURL=lagerort.js.map